import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE = (process.env.REACT_APP_RANGEGRID_API_BASE || "").trim().replace(/\/$/, "");
const DEV_USER_ID = process.env.REACT_APP_RANGEGRID_DEV_USER_ID || "";

function getApiBase() {
  if (!API_BASE) {
    throw new Error("RangeGrid API URL is not configured. Set REACT_APP_RANGEGRID_API_BASE to the deployed operations API invoke URL.");
  }

  return API_BASE;
}

async function getAuthHeaders(required = false) {
  if (DEV_USER_ID.trim()) {
    return { "x-rangegrid-dev-user-id": DEV_USER_ID.trim() };
  }

  if (!required) {
    return {};
  }

  try {
    const session = await fetchAuthSession();
    const token =
      session?.tokens?.idToken?.toString() ||
      session?.tokens?.accessToken?.toString() ||
      "";

    if (token) {
      return { authorization: `Bearer ${token}` };
    }
  } catch {
    // Fall through to required check below.
  }

  if (required) {
    throw new Error("Sign in required");
  }

  return {};
}

async function request(path, options = {}) {
  const { auth = true, headers: providedHeaders = {}, ...fetchOptions } = options;
  const authHeaders = await getAuthHeaders(auth);
  let response;
  try {
    response = await fetch(`${getApiBase()}${path}`, {
      headers: {
        "content-type": "application/json",
        ...authHeaders,
        ...providedHeaders
      },
      ...fetchOptions
    });
  } catch {
    throw new Error("RangeGrid could not reach the server. Check the connection and try again.");
  }
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const fallbackMessages = {
      429: "RangeGrid is receiving too many requests right now. Wait a moment and try again.",
      500: "RangeGrid hit a server problem. Try again, and contact support if it keeps happening.",
      502: "RangeGrid could not reach one of its services. Try again in a moment.",
      503: "RangeGrid is temporarily busy. Try again in a moment.",
      504: "RangeGrid took too long to respond. Try again in a moment."
    };
    const message = payload?.error?.message || fallbackMessages[response.status] || `Request failed (${response.status})`;
    throw new Error(message);
  }

  return payload;
}

function buildQueryString(params = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    searchParams.set(key, value);
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
}

export async function listMyRanges() {
  const payload = await request("/me/ranges");
  return payload.data || [];
}

export async function createRange(range) {
  const payload = await request("/ranges", {
    method: "POST",
    body: JSON.stringify(range)
  });
  return payload.data;
}

export async function updateRangeLifecycle(rangeId, lifecycle) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/lifecycle`, {
    method: "PATCH",
    body: JSON.stringify(lifecycle)
  });
  return payload.data;
}

export async function updateRangeProfile(rangeId, profile) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/profile`, {
    method: "PATCH",
    body: JSON.stringify(profile)
  });
  return payload.data;
}

export async function updateRangeBranding(rangeId, branding) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/branding`, {
    method: "PATCH",
    body: JSON.stringify(branding)
  });
  return payload.data;
}

export async function updateRangeProductionMenu(rangeId, patch) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/production-menu`, {
    method: "PATCH",
    body: JSON.stringify(patch)
  });
  return payload.data;
}

export async function getRangePublicDomain(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/public-domain`);
  return payload.data;
}

export async function setupRangePublicDomain(rangeId, domain) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/public-domain/setup`, {
    method: "POST",
    body: JSON.stringify({ domain })
  });
  return payload.data;
}

export async function checkRangePublicDomain(rangeId, domain = "") {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/public-domain/check`, {
    method: "POST",
    body: JSON.stringify(domain ? { domain } : {})
  });
  return payload.data;
}

export async function disconnectRangePublicDomain(rangeId, domain = "") {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/public-domain/disconnect`, {
    method: "POST",
    body: JSON.stringify(domain ? { domain } : {})
  });
  return payload.data;
}

export async function getBusinessHours(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/business-hours`);
  return payload.data;
}

export async function getRangeReadiness(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/readiness`);
  return payload.data;
}

export async function updateBusinessHours(rangeId, businessHours) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/business-hours`, {
    method: "PATCH",
    body: JSON.stringify(businessHours)
  });
  return payload.data;
}

export async function getPublicRange(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}`, { auth: false });
  return payload.data;
}

export async function getPublicDomainContext(hostname) {
  const payload = await request(`/public/domains/${encodeURIComponent(hostname)}`, { auth: false });
  return payload.data;
}

export async function getPublicRangeMembership(slug, options = {}) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/membership${buildQueryString({ chapterId: options.chapterId })}`,
    { auth: false }
  );
  return payload.data;
}

export async function getPublicRangeProfile(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/profile`, { auth: false });
  return payload.data;
}

export async function getPublicEvents(slug, options = {}) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/events${buildQueryString({ chapterId: options.chapterId || options.homeChapterId })}`,
    { auth: options.auth === true }
  );
  return payload.data;
}

export async function createPublicLead(slug, lead) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/leads`, {
    auth: false,
    method: "POST",
    body: JSON.stringify(lead)
  });
  return payload.data;
}

export async function unsubscribePublicCommunication(slug, payloadBody) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/communications/unsubscribe`, {
    auth: false,
    method: "POST",
    body: JSON.stringify(payloadBody)
  });
  return payload.data;
}

export async function createPlatformLead(lead) {
  const payload = await request("/platform/leads", {
    auth: false,
    method: "POST",
    body: JSON.stringify(lead)
  });
  return payload.data;
}

export async function createPublicDonation(slug, donation) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/donations`, {
    auth: false,
    method: "POST",
    body: JSON.stringify(donation)
  });
  return payload.data;
}

export async function getPublicDonationReceipt(slug, donationId, sessionId) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/donations/${encodeURIComponent(donationId)}/receipt?session_id=${encodeURIComponent(sessionId)}`,
    { auth: false }
  );
  return payload.data;
}

export async function getPublicEventRegistrationStatus(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/events/registrations`);
  return payload.data;
}

export async function getMemberEventRegistrations(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/member/event-registrations`);
  return payload.data;
}

export async function getPublicMemberSummary(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/member/summary`);
  return payload.data;
}

export async function getPublicReservationEligibility(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/reservations/eligibility`);
  return payload.data;
}

export async function listPublicResourceReservations(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/reservations`);
  return payload.data;
}

export async function getMemberReservations(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/member/reservations`);
  return payload.data;
}

export async function createPublicResourceReservation(slug, reservation) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/reservations`, {
    method: "POST",
    body: JSON.stringify(reservation)
  });
  return payload.data;
}

export async function cancelPublicResourceReservation(slug, reservationId, options = {}) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/reservations/${encodeURIComponent(reservationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({
        status: "cancelled",
        reason: options.reason
      })
    }
  );
  return payload.data;
}

export async function reschedulePublicResourceReservation(slug, reservationId, reservation) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/reservations/${encodeURIComponent(reservationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({
        action: "reschedule",
        ...reservation
      })
    }
  );
  return payload.data;
}

export async function createPublicEventRegistration(slug, eventId) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/events/${encodeURIComponent(eventId)}/registrations`,
    {
      method: "POST",
      body: JSON.stringify({})
    }
  );
  return payload.data;
}

export async function cancelPublicEventRegistration(slug, registrationId, options = {}) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/events/registrations/${encodeURIComponent(registrationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({
        status: "cancelled",
        reason: options.reason
      })
    }
  );
  return payload.data;
}

export async function createMembershipSignupIntent(slug, membershipTypeId, options = {}) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/membership-signup-intents`,
    {
      method: "POST",
      auth: options.auth ?? true,
      body: JSON.stringify({
        membershipTypeId,
        homeChapterId: options.homeChapterId || options.chapterId,
        source: options.source,
        campaign: options.campaign
      })
    }
  );
  return payload.data;
}

export async function createMembershipRenewalCheckout(slug, membershipTypeId) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/renewals`, {
    method: "POST",
    body: JSON.stringify({ membershipTypeId })
  });
  return payload.data;
}

export async function createMembershipPlanChangeCheckout(slug, membershipTypeId) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/plan-changes`, {
    method: "POST",
    body: JSON.stringify({ membershipTypeId })
  });
  return payload.data;
}

export async function createMembershipBalanceCheckout(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/balance-checkouts`, {
    method: "POST"
  });
  return payload.data;
}

export async function getMembershipApplicantStatus(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/status`);
  return payload.data;
}

export async function updatePublicMemberProfile(slug, profile) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/profile`, {
    method: "PATCH",
    body: JSON.stringify(profile)
  });
  return payload.data;
}

export async function getPublicMembershipPayments(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/payments`);
  return payload.data;
}

export async function completeMembershipCheckout(slug, sessionId) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/checkout-completions`, {
    method: "POST",
    body: JSON.stringify({ sessionId })
  });
  return payload.data;
}

export async function acknowledgeMembershipWaiver(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/waiver-acknowledgement`, {
    method: "POST"
  });
  return payload.data;
}

export async function submitMembershipPrequalification(slug, responses, applicationResponses = []) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/prequalification`, {
    method: "POST",
    body: JSON.stringify({ responses, applicationResponses })
  });
  return payload.data;
}

export async function startMembershipBackgroundCheck(slug, candidate) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/background-check/start`, {
    method: "POST",
    body: JSON.stringify(candidate)
  });
  return payload.data;
}

export async function redeemPublicGuestPass(slug, redemption) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/guest-passes/redeem`, {
    method: "POST",
    body: JSON.stringify(redemption)
  });
  return payload.data;
}

export async function acknowledgePublicGuestWaiver(slug, entryId) {
  const payload = await request(
    `/public/ranges/${encodeURIComponent(slug)}/membership/guest-passes/${encodeURIComponent(entryId)}/waiver-acknowledgement`,
    { method: "POST" }
  );
  return payload.data;
}

export async function requestPublicQualification(slug, requestBody) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/membership/qualification-requests`, {
    method: "POST",
    body: JSON.stringify(requestBody)
  });
  return payload.data;
}

export async function getRangeContext(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/context`);
  return payload.data;
}

export async function listApplications() {
  const payload = await request("/applications");
  return payload.data || [];
}

export async function listRangeApplications(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/applications`);
  return payload.data || [];
}

export async function getInsightsSummary(rangeId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/insights/summary${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function getInsightsMemberJourney(rangeId, memberId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/insights/members/${encodeURIComponent(memberId)}/journey${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function listInsightsMembers(rangeId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/insights/members${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function listChapters(rangeId, filters = {}) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/chapters${buildQueryString(filters)}`);
  return payload.data;
}

export async function createChapter(rangeId, chapter) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/chapters`, {
    method: "POST",
    body: JSON.stringify(chapter)
  });
  return payload.data;
}

export async function updateChapter(rangeId, chapterId, chapter) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/chapters/${encodeURIComponent(chapterId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(chapter)
    }
  );
  return payload.data;
}

export async function getPublicChapters(slug) {
  const payload = await request(`/public/ranges/${encodeURIComponent(slug)}/chapters`, { auth: false });
  return payload.data;
}

export async function listRangeAccess(rangeId, filters = {}) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/access${buildQueryString(filters)}`);
  return payload.data;
}

export async function downloadRangeAccessCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/access/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-access.csv"
  };
}

export async function listAuditEvents(rangeId, filters = {}, limit = 50) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/audit${buildQueryString({ ...filters, limit })}`
  );
  return payload.data;
}

export async function listOperationNotes(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/operations/notes`);
  return payload.data;
}

export async function createOperationNote(rangeId, note) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/operations/notes`, {
    method: "POST",
    body: JSON.stringify(note)
  });
  return payload.data;
}

export async function downloadAuditCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/audit/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-audit-report.csv"
  };
}

export async function listRangeLeads(rangeId, filters = {}, limit = 100) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/leads${buildQueryString({ ...filters, limit })}`
  );
  return payload.data || { leads: [], summary: {} };
}

export async function getRangeLeadSettings(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/lead-settings`);
  return payload.data || { interests: [] };
}

export async function updateRangeLeadSettings(rangeId, settings) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/lead-settings`, {
    method: "PATCH",
    body: JSON.stringify(settings)
  });
  return payload.data || { interests: [] };
}

export async function downloadRangeLeadsCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/leads/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-leads.csv"
  };
}

export async function updateRangeLead(rangeId, leadId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/leads/${encodeURIComponent(leadId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function sendRangeLeadEmail(rangeId, leadId, email) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/leads/${encodeURIComponent(leadId)}/email`,
    {
      method: "POST",
      body: JSON.stringify(email)
    }
  );
  return payload.data;
}

export async function listNotifications(rangeId, filters = {}, limit = 50) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/notifications${buildQueryString({ ...filters, limit })}`
  );
  return payload.data || { notifications: [], summary: {} };
}

export async function downloadNotificationsCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/notifications/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-notifications.csv"
  };
}

export async function queueCustomNotification(rangeId, notification) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/notifications`,
    {
      method: "POST",
      body: JSON.stringify(notification)
    }
  );
  return payload.data;
}

export async function previewCommunicationAudience(rangeId, params = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/communications/audience-preview${buildQueryString(params)}`
  );
  return payload.data || {
    eligibleCount: 0,
    skippedCount: 0,
    skippedByReason: {},
    recipients: []
  };
}

export async function sendCommunicationBroadcast(rangeId, broadcast) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/communications/broadcasts`,
    {
      method: "POST",
      body: JSON.stringify(broadcast)
    }
  );
  return payload.data;
}

export async function updateNotificationStatus(rangeId, notificationId, status) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/notifications/${encodeURIComponent(notificationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ status })
    }
  );
  return payload.data;
}

export async function deliverNotification(rangeId, notificationId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/notifications/${encodeURIComponent(notificationId)}/deliver`,
    {
      method: "POST"
    }
  );
  return payload.data;
}

export async function getNotificationPreferences(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/notification-preferences`);
  return payload.data;
}

export async function updateNotificationPreferences(rangeId, preferences) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/notification-preferences`, {
    method: "PATCH",
    body: JSON.stringify(preferences)
  });
  return payload.data;
}

export async function startSenderEmailVerification(rangeId, email) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/notification-preferences/sender-verification`,
    {
      method: "POST",
      body: JSON.stringify({ email })
    }
  );
  return payload.data;
}

export async function refreshSenderEmailVerification(rangeId, email) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/notification-preferences/sender-refresh`,
    {
      method: "POST",
      body: JSON.stringify({ email })
    }
  );
  return payload.data;
}

export async function getRangePayments(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/payments`);
  return payload.data;
}

export async function listDonations(rangeId, params = {}) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/donations${buildQueryString(params)}`);
  return payload.data || { donations: [], summary: {} };
}

export async function downloadDonationsCsv(rangeId, params = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/donations/export${buildQueryString(params)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }

  return {
    body,
    filename: "rangegrid-donations.csv"
  };
}

export async function getDonationSettings(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/donations/settings`);
  return payload.data;
}

export async function updateDonationSettings(rangeId, settings) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/donations/settings`, {
    method: "PATCH",
    body: JSON.stringify(settings)
  });
  return payload.data;
}

export async function createDonation(rangeId, donation) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/donations`, {
    method: "POST",
    body: JSON.stringify(donation)
  });
  return payload.data;
}

export async function getDonationReceipt(rangeId, donationId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/donations/${encodeURIComponent(donationId)}/receipt`
  );
  return payload.data;
}

export async function recordDonationRefund(rangeId, donationId, refund) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/donations/${encodeURIComponent(donationId)}/refund`,
    {
      method: "PATCH",
      body: JSON.stringify(refund)
    }
  );
  return payload.data;
}

export async function createStripeConnectAccountLink(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/payments/stripe/connect-account`, {
    method: "POST"
  });
  return payload.data;
}

export async function createStripeConnectAccountSession(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/payments/stripe/account-session`, {
    method: "POST"
  });
  return payload.data;
}

export async function refreshStripeConnectAccount(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/payments/stripe/refresh`, {
    method: "POST"
  });
  return payload.data;
}

export async function listPaymentEvents(rangeId, params = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/payments/events${buildQueryString(params)}`
  );
  return payload.data || { events: [], summary: {} };
}

export async function listPaymentIssues(rangeId, params = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/payments/issues${buildQueryString(params)}`
  );
  return payload.data || { issues: [], summary: {} };
}

export async function downloadPaymentEventsCsv(rangeId, params = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/payments/events/export${buildQueryString(params)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-payment-events.csv"
  };
}

export async function listPaymentReceipts(rangeId, params = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/payments/receipts${buildQueryString(params)}`
  );
  return payload.data || { receipts: [], summary: {} };
}

export async function downloadPaymentReceiptsCsv(rangeId, params = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/payments/receipts/export${buildQueryString(params)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return {
    body: await response.text(),
    filename: "rangegrid-payment-receipts.csv"
  };
}

export async function downloadPaymentReconciliationCsv(rangeId, params = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/payments/reconciliation/export${buildQueryString(params)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return {
    body: await response.text(),
    filename: "rangegrid-payment-reconciliation.csv"
  };
}

export async function downloadPaymentPeriodClosesCsv(rangeId) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/payments/reconciliation/closes/export`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return {
    body: await response.text(),
    filename: "rangegrid-payment-period-closes.csv"
  };
}

export async function syncPaymentReconciliation(rangeId, params = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/payments/reconciliation/sync${buildQueryString(params)}`,
    {
      method: "POST"
    }
  );
  return payload.data;
}

export async function closePaymentReconciliationPeriod(rangeId, close) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/payments/reconciliation/close`,
    {
      method: "POST",
      body: JSON.stringify(close)
    }
  );
  return payload.data;
}

export async function refundPaymentReceipt(rangeId, receiptId, refund) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/payments/receipts/${encodeURIComponent(receiptId)}/refund`,
    {
      method: "POST",
      body: JSON.stringify(refund)
    }
  );
  return payload.data;
}

export async function updatePaymentReceiptReconciliation(rangeId, receiptId, reconciliation) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/payments/receipts/${encodeURIComponent(receiptId)}/reconciliation`,
    {
      method: "PATCH",
      body: JSON.stringify(reconciliation)
    }
  );
  return payload.data;
}

export async function grantRangeAccess(rangeId, accessGrant) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/access`, {
    method: "POST",
    body: JSON.stringify(accessGrant)
  });
  return payload.data;
}

export async function updateRangeAccess(rangeId, accessUserId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/access/${encodeURIComponent(accessUserId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function enableRangeApplication(rangeId, applicationId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/applications/${encodeURIComponent(applicationId)}/enable`,
    { method: "POST" }
  );
  return payload.data;
}

export async function disableRangeApplication(rangeId, applicationId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/applications/${encodeURIComponent(applicationId)}/disable`,
    { method: "POST" }
  );
  return payload.data;
}

export async function getMembershipSummary(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/membership/summary`);
  return payload.data;
}

export async function updateMembershipConfig(rangeId, patch) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/membership/config`, {
    method: "PATCH",
    body: JSON.stringify(patch)
  });
  return payload.data;
}

export async function getMembershipBackgroundCheckSettings(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/membership/background-check`);
  return payload.data;
}

export async function updateMembershipBackgroundCheckSettings(rangeId, patch) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/membership/background-check`, {
    method: "PATCH",
    body: JSON.stringify(patch)
  });
  return payload.data;
}

export async function testMembershipBackgroundCheckSettings(rangeId) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/membership/background-check/checkr/test`, {
    method: "POST"
  });
  return payload.data;
}

export async function createMembershipDocumentUploadUrl(rangeId, document) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/membership/documents/upload-url`, {
    method: "POST",
    body: JSON.stringify(document)
  });
  return payload.data;
}

export async function listMembers(rangeId, filters = {}) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/members${buildQueryString(filters)}`);
  return payload.data || [];
}

export async function downloadMembersCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/members/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-members.csv"
  };
}

export async function getMemberDetail(rangeId, memberId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/detail`
  );
  return payload.data;
}

export async function getMemberTimeline(rangeId, memberId, limit = 50) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/timeline?limit=${encodeURIComponent(limit)}`
  );
  return payload.data || { timeline: [], summary: {} };
}

export async function createMember(rangeId, member) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/members`, {
    method: "POST",
    body: JSON.stringify(member)
  });
  return payload.data;
}

export async function previewMemberImport(rangeId, csv) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/members/import-preview`, {
    method: "POST",
    body: JSON.stringify({ csv })
  });
  return payload.data;
}

export async function updateMemberReview(rangeId, memberId, reviewPatch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/review`,
    {
      method: "PATCH",
      body: JSON.stringify(reviewPatch)
    }
  );
  return payload.data;
}

export async function retryMemberBackgroundCheck(rangeId, memberId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/background-check/retry`,
    { method: "POST" }
  );
  return payload.data;
}

export async function updateMemberStatus(rangeId, memberId, statusPatch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/status`,
    {
      method: "PATCH",
      body: JSON.stringify(statusPatch)
    }
  );
  return payload.data;
}

export async function updateMemberProfile(rangeId, memberId, profilePatch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(profilePatch)
    }
  );
  return payload.data;
}

export async function renewMember(rangeId, memberId, renewal) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/renewal`,
    {
      method: "POST",
      body: JSON.stringify(renewal)
    }
  );
  return payload.data;
}

export async function changeMemberPlan(rangeId, memberId, planChange) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/plan-change`,
    {
      method: "POST",
      body: JSON.stringify(planChange)
    }
  );
  return payload.data;
}

export async function updateMemberGuestPasses(rangeId, memberId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/guest-passes`,
    {
      method: "POST",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function downloadMemberGuestPassesCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/members/guest-passes/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-member-guest-passes.csv"
  };
}

export async function addHouseholdMember(rangeId, memberId, householdMember) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/household-members`,
    {
      method: "POST",
      body: JSON.stringify(householdMember)
    }
  );
  return payload.data;
}

export async function updateHouseholdMember(rangeId, memberId, householdMemberId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/household-members/${encodeURIComponent(householdMemberId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function downloadMemberHouseholdCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/members/household/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-member-household.csv"
  };
}

export async function addMemberFlag(rangeId, memberId, flag) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/flags`,
    {
      method: "POST",
      body: JSON.stringify(flag)
    }
  );
  return payload.data;
}

export async function updateMemberFlag(rangeId, memberId, flagId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/flags/${encodeURIComponent(flagId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function addMemberDocument(rangeId, memberId, document) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/documents`,
    {
      method: "POST",
      body: JSON.stringify(document)
    }
  );
  return payload.data;
}

export async function updateMemberDocument(rangeId, memberId, documentId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/documents/${encodeURIComponent(documentId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function downloadMemberFlagsCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/members/flags/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-member-flags.csv"
  };
}

export async function addMemberQualification(rangeId, memberId, qualification) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/qualifications`,
    {
      method: "POST",
      body: JSON.stringify(qualification)
    }
  );
  return payload.data;
}

export async function updateMemberQualification(rangeId, memberId, qualificationId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/members/${encodeURIComponent(memberId)}/qualifications/${encodeURIComponent(qualificationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function downloadMemberQualificationsCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/members/qualifications/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-member-qualifications.csv"
  };
}

export async function createMembershipType(rangeId, membershipType) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/membership-types`, {
    method: "POST",
    body: JSON.stringify(membershipType)
  });
  return payload.data;
}

export async function updateMembershipType(rangeId, membershipTypeId, membershipType) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/membership-types/${encodeURIComponent(membershipTypeId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(membershipType)
    }
  );
  return payload.data;
}

export async function setDefaultMembershipType(rangeId, membershipTypeId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/membership-types/${encodeURIComponent(membershipTypeId)}/default`,
    { method: "POST" }
  );
  return payload.data;
}

export async function getResourceSummary(rangeId, filters = {}) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/resources${buildQueryString(filters)}`);
  return payload.data;
}

export async function downloadResourcesCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/resources/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-resources.csv"
  };
}

export async function getResourceDetail(rangeId, resourceId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resources/${encodeURIComponent(resourceId)}`
  );
  return payload.data;
}

export async function createResource(rangeId, resource) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/resources`, {
    method: "POST",
    body: JSON.stringify(resource)
  });
  return payload.data;
}

export async function updateResource(rangeId, resourceId, resource) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resources/${encodeURIComponent(resourceId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(resource)
    }
  );
  return payload.data;
}

export async function updateResourceVisibility(rangeId, resourceIds = [], publicationStatus = "open", options = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resources/visibility`,
    {
      method: "PATCH",
      body: JSON.stringify({
        resourceIds,
        publicationStatus,
        rootResourceId: options.rootResourceId,
        includeAncestors: options.includeAncestors,
        includeDescendants: options.includeDescendants
      })
    }
  );
  return payload.data;
}

export async function updateResourceSectionCommand(rangeId, resourceId, command) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resources/${encodeURIComponent(resourceId)}/section-command`,
    {
      method: "POST",
      body: JSON.stringify(command)
    }
  );
  return payload.data;
}

export async function deleteResource(rangeId, resourceId) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resources/${encodeURIComponent(resourceId)}`,
    {
      method: "DELETE"
    }
  );
  return payload.data;
}

export async function listResourceBlocks(rangeId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resource-blocks${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function downloadResourceBlocksCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/resource-blocks/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }

  return {
    body,
    filename: "rangegrid-resource-blocks.csv"
  };
}

export async function createResourceBlock(rangeId, block) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/resource-blocks`, {
    method: "POST",
    body: JSON.stringify(block)
  });
  return payload.data;
}

export async function updateResourceBlock(rangeId, blockId, block) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resource-blocks/${encodeURIComponent(blockId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(block)
    }
  );
  return payload.data;
}

export async function checkResourceAvailability(rangeId, resourceId, { startsAt, endsAt }) {
  const params = new URLSearchParams({
    startsAt,
    endsAt
  });
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resources/${encodeURIComponent(resourceId)}/availability?${params.toString()}`
  );
  return payload.data;
}

export async function listResourceReservations(rangeId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resource-reservations${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function getResourceReservationConflicts(rangeId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resource-reservations/conflicts${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function createResourceReservation(rangeId, reservation) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/resource-reservations`, {
    method: "POST",
    body: JSON.stringify(reservation)
  });
  return payload.data;
}

export async function updateResourceReservation(rangeId, reservationId, reservation) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/resource-reservations/${encodeURIComponent(reservationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(reservation)
    }
  );
  return payload.data;
}

export async function downloadResourceReservationsCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/resource-reservations/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }

  return {
    body,
    filename: "rangegrid-resource-reservations.csv"
  };
}

export async function listEvents(rangeId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function downloadEventsCsv(rangeId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/events/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }
  return {
    body,
    filename: "rangegrid-events.csv"
  };
}

export async function createEvent(rangeId, event) {
  const payload = await request(`/ranges/${encodeURIComponent(rangeId)}/events`, {
    method: "POST",
    body: JSON.stringify(event)
  });
  return payload.data;
}

export async function updateEvent(rangeId, eventId, event) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(event)
    }
  );
  return payload.data;
}

export async function signOffEvent(rangeId, eventId, signoff = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/signoff`,
    {
      method: "POST",
      body: JSON.stringify(signoff)
    }
  );
  return payload.data;
}

export async function reviewEventApproval(rangeId, eventId, decision, review = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/approval`,
    {
      method: "POST",
      body: JSON.stringify({
        ...review,
        decision
      })
    }
  );
  return payload.data;
}

export async function listNonMemberAttendees(rangeId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/non-member-attendees${buildQueryString(filters)}`
  );
  return payload.data || { attendees: [], summary: {} };
}

export async function sendNonMemberAttendeeEmail(rangeId, attendanceId, email) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/non-member-attendees/${encodeURIComponent(attendanceId)}/email`,
    {
      method: "POST",
      body: JSON.stringify(email)
    }
  );
  return payload.data;
}

export async function listEventRegistrations(rangeId, eventId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/registrations${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function createEventRegistration(rangeId, eventId, registration) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/registrations`,
    {
      method: "POST",
      body: JSON.stringify(registration)
    }
  );
  return payload.data;
}

export async function updateEventRegistration(rangeId, eventId, registrationId, patch) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(registrationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch)
    }
  );
  return payload.data;
}

export async function promoteNextEventWaitlist(rangeId, eventId, reason = "") {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/registrations/promote-next`,
    {
      method: "POST",
      body: JSON.stringify({ reason })
    }
  );
  return payload.data;
}

export async function queueEventReminders(rangeId, eventId, message = "") {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/notifications/reminders`,
    {
      method: "POST",
      body: JSON.stringify({ message })
    }
  );
  return payload.data;
}

export async function downloadEventRegistrationsCsv(rangeId, eventId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/registrations/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }

  return {
    body,
    filename: "rangegrid-event-registrations.csv"
  };
}

export async function downloadEventAttendanceCsv(rangeId, eventId, filters = {}) {
  const authHeaders = await getAuthHeaders(true);
  const response = await fetch(
    `${getApiBase()}/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/attendance/export${buildQueryString(filters)}`,
    {
      headers: {
        ...authHeaders
      }
    }
  );
  const body = await response.text();
  if (!response.ok) {
    throw new Error(body || `Request failed (${response.status})`);
  }

  return {
    body,
    filename: "rangegrid-event-attendance.csv"
  };
}

export async function listEventAttendance(rangeId, eventId, filters = {}) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/attendance${buildQueryString(filters)}`
  );
  return payload.data;
}

export async function recordEventAttendance(rangeId, eventId, attendance) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/attendance`,
    {
      method: "POST",
      body: JSON.stringify(attendance)
    }
  );
  return payload.data;
}

export async function updateEventAttendance(rangeId, eventId, attendanceId, attendance) {
  const payload = await request(
    `/ranges/${encodeURIComponent(rangeId)}/events/${encodeURIComponent(eventId)}/attendance/${encodeURIComponent(attendanceId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(attendance)
    }
  );
  return payload.data;
}
